// src/pages/PlanPage.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const PlanPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [customerName, setCustomerName] = useState('');
  const [recommendationMd, setRecommendationMd] = useState('');
  const [cards, setCards] = useState([]); // products to render
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        // 1) Prefer state passed from quiz → results navigation
        if (location.state?.recommendationMd || location.state?.recommendation) {
          setCustomerName(location.state?.quizData?.pet_name || 'your pup');
          setRecommendationMd(location.state.recommendationMd || location.state.recommendation || '');

          // Still load catalog to show products
          const catalog = await fetchCatalog();
          const picks = pickFromText(location.state.recommendationMd || location.state.recommendation || '', catalog);
          setCards(picks.length ? picks : catalog.slice(0, 6));
          setIsLoading(false);
          return;
        }

        // 2) Otherwise, fetch by session id
        const search = new URLSearchParams(location.search);
        const sessionId = search.get('session_id');
        if (!sessionId) throw new Error('No session_id in URL');

        const sessRes = await fetch(`/api/get-session?session_id=${encodeURIComponent(sessionId)}`);
        if (!sessRes.ok) throw new Error('Failed to fetch session');

        const sessJson = await sessRes.json();
        const data = sessJson?.data || {};
        setCustomerName(data.pet_name || data.customer_name || 'your pet');

        // Many backends store plan text under different keys — try a few:
        const md =
          data.recommendation_markdown ||
          data.recommendation_md ||
          data.recommendation ||
          data.recommendation_summary ||
          '';
        setRecommendationMd(md);

        const catalog = await fetchCatalog();
        const picks = pickFromText(md, catalog);
        setCards(picks.length ? picks : catalog.slice(0, 6));
      } catch (e) {
        console.error(e);
        setErr('Failed to load your plan. Please retry the quiz.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [location]);

  // --- helpers
  async function fetchCatalog() {
    const res = await fetch('/api/get-products');
    const json = await res.json();
    return Array.isArray(json?.products) ? json.products : [];
  }

  // Extract likely product names from markdown & fuzzy-match
  function pickFromText(md, catalog) {
    if (!md || !catalog.length) return [];
    // grab bold segments **Like This** and lines ending before ":" (Name: reason)
    const boldNames = Array.from(md.matchAll(/\*\*([^*]+)\*\*/g)).map(m => m[1].trim());
    const colonNames = md
      .split(/\n+/)
      .map(l => l.split(':')[0]?.trim())
      .filter(Boolean);

    const candidates = [...new Set([...boldNames, ...colonNames])]
      .map(s => s.replace(/^[-–•]\s*/, '')) // trim bullets
      .filter(s => s.length > 2);

    const seen = new Set();
    const picks = [];

    const matchOne = (want) =>
      catalog.find(p =>
        (p.name || '').toLowerCase().includes(want.toLowerCase()) ||
        want.toLowerCase().includes((p.name || '').toLowerCase())
      );

    for (const want of candidates) {
      const hit = matchOne(want);
      if (hit && !seen.has(hit.id)) {
        seen.add(hit.id);
        picks.push(hit);
      }
      if (picks.length >= 6) break;
    }
    return picks;
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <p>Loading your personalized recommendations…</p>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <div className="card" style={{ maxWidth: 480, padding: 24 }}>
          <h3 style={{ marginBottom: 12, color: '#e74c3c' }}>Oops!</h3>
          <p style={{ marginBottom: 16 }}>{err}</p>
          <button className="btn btn-primary" onClick={() => navigate('/quiz')}>Take Quiz Again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background-light)' }}>
      {/* header */}
      <header style={{ background: 'linear-gradient(135deg,#0d47a1,#1976d2)', color: 'white', padding: '40px 0' }}>
        <div className="container">
          <h1 style={{ margin: 0, marginBottom: 8 }}>Perfect Food Plan for {customerName}!</h1>
          <p style={{ opacity: 0.9, margin: 0 }}>
            Based on your quiz responses, we’ve curated the best nutrition options.
          </p>
        </div>
      </header>

      {/* recommendation markdown */}
      <section style={{ padding: '32px 0' }}>
        <div className="container">
          <div className="card" style={{ padding: 24, borderLeft: '4px solid #1976d2', background: '#e8f0fe' }}>
            <ReactMarkdown
              className="markdown"
              remarkPlugins={[remarkGfm]}
            >
              {recommendationMd || '*Your personalized plan will appear here.*'}
            </ReactMarkdown>
          </div>
        </div>
      </section>

      {/* products */}
      <section style={{ paddingBottom: 56 }}>
        <div className="container">
          <h2 className="text-center">Featured Foods for {customerName}</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
              gap: 24,
              marginTop: 24
            }}
          >
            {cards.map((p, i) => {
              const shopUrl = p.affiliate_link || p.amazon_link || '#';
              return (
                <div key={p.id || i} className="card" style={{ padding: 16 }}>
                  <div className="text-center" style={{ marginBottom: 12 }}>
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 10 }}
                    />
                  </div>
                  <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, background: '#1976d2', color: 'white', padding: '2px 8px', borderRadius: 999 }}>
                      {p.brand}
                    </span>
                  </div>
                  <h3 style={{ fontSize: 18, textAlign: 'center', margin: '6px 0 8px' }}>{p.name}</h3>
                  <p style={{ color: '#555', fontSize: 14, textAlign: 'center', minHeight: 44 }}>{p.description}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <strong>{p.price}</strong>
                    {p.rating ? <span>★ {p.rating}</span> : <span />}
                  </div>
                  <a
                    href={shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ marginTop: 12, width: '100%', display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}
                  >
                    Shop Now →
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlanPage;
